import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import api from '@/utils/api';
import { ChevronRight, ChevronLeft, FileText, Calendar, ArrowRight, Shield, ExternalLink } from 'lucide-react';

const HERO_SLIDES = [
  { bg: 'https://customer-assets.emergentagent.com/job_cd6c22bb-693b-41c8-a61a-f7b4e9542f9d/artifacts/t1fyhjpv_ANGOLA%20DAY%2010%281%29.jpg', tag: 'Diplomacia', pos: 'center 30%' },
  { bg: 'https://customer-assets.emergentagent.com/job_cd6c22bb-693b-41c8-a61a-f7b4e9542f9d/artifacts/ljobel9t_ANGOLA%20DAY%203%281%29.jpg', tag: 'Angola Day', pos: 'center 20%' },
  { bg: 'https://customer-assets.emergentagent.com/job_cd6c22bb-693b-41c8-a61a-f7b4e9542f9d/artifacts/h9ezsh1x_ANGOLA%20DAY%20161.jpg', tag: 'Cultura Angolana', pos: 'center 25%' },
  { bg: 'https://customer-assets.emergentagent.com/job_cd6c22bb-693b-41c8-a61a-f7b4e9542f9d/artifacts/oezuuv49_ANGOLA%20DAY%20139.jpg', tag: 'Comunidade', pos: 'center 30%' },
  { bg: 'https://customer-assets.emergentagent.com/job_cd6c22bb-693b-41c8-a61a-f7b4e9542f9d/artifacts/hxtprigz_ANGOLA%20DAY%20134.jpg', tag: 'Tradição', pos: 'center 40%' },
];

// Featured External News for Homepage
const FEATURED_NEWS = [
  {
    id: 1,
    title: "Embaixadora de Angola em Espanha apresenta cartas Figuradas à Ministra dos Negócios Estrangeiros de Andorra",
    source: "A Voz da Diáspora",
    sourceUrl: "https://vozdadiaspora.com/2026/02/embaixadora-de-angola-em-espanha-apresenta-cartas-figuradas-a-ministra-dos-negocios-estrangeiros-de-andorra/",
    date: "19 Fev 2026",
    image: "https://vozdadiaspora.com/wp-content/uploads/2026/02/cartas-andorra-991x564.jpeg"
  },
  {
    id: 2,
    title: "Diplomata realça afirmação de África como espaço de crescimento real",
    source: "ANGOP",
    sourceUrl: "https://www.angop.ao/noticias/economia/diplomata-realca-afirmacao-de-africa-como-espaco-de-crescimento-real/",
    date: "Fev 2026",
    image: "https://customer-assets.emergentagent.com/job_50e85d91-1f1d-48c5-a645-eaeea3c1f4b7/artifacts/mc6fn6ht_Angop.png"
  },
  {
    id: 3,
    title: "Plaza de Toros de Las Ventas acogerá el Día de Angola - 50º Aniversario",
    source: "El Trapezio",
    sourceUrl: "https://eltrapezio.eu/es/2025/10/25/plaza-de-toros-de-las-ventas-acogera-por-primera-vez-el-dia-de-angola-con-motivo-del-50o-aniversario-de-su-independencia/",
    date: "25 Out 2025",
    image: "https://customer-assets.emergentagent.com/job_792e387f-b4b3-43c6-bf9d-46ac5c601086/artifacts/4kxfa3mv_50%20Aniversario_Independencia.jpeg"
  }
];

// Services Carousel - Continuous infinite slideshow
function ServicesCarousel() {
  const [services, setServices] = useState([]);
  const trackRef = useRef(null);
  const animRef = useRef(null);

  useEffect(() => {
    api.get('/services').then(r => setServices(r.data || [])).catch(console.error);
  }, []);

  useEffect(() => {
    if (services.length === 0 || !trackRef.current) return;
    const track = trackRef.current;
    let pos = 0;
    const speed = 0.5; // px per frame

    const animate = () => {
      pos += speed;
      // When we've scrolled past the first set, reset seamlessly
      if (pos >= track.scrollWidth / 2) pos = 0;
      track.style.transform = `translateX(-${pos}px)`;
      animRef.current = requestAnimationFrame(animate);
    };

    animRef.current = requestAnimationFrame(animate);

    const pause = () => cancelAnimationFrame(animRef.current);
    const resume = () => { animRef.current = requestAnimationFrame(animate); };
    track.addEventListener('mouseenter', pause);
    track.addEventListener('mouseleave', resume);

    return () => {
      cancelAnimationFrame(animRef.current);
      track.removeEventListener('mouseenter', pause);
      track.removeEventListener('mouseleave', resume);
    };
  }, [services]);

  if (services.length === 0) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[1,2,3,4].map(i => <div key={i} className="h-24 bg-gray-100 rounded-xl animate-pulse" />)}
      </div>
    );
  }

  // Duplicate the services for seamless loop
  const doubled = [...services, ...services];

  return (
    <div data-testid="services-carousel" className="overflow-hidden">
      <div ref={trackRef} className="flex gap-4 will-change-transform" style={{ width: 'max-content' }}>
        {doubled.map((svc, i) => (
          <Link key={`${svc.id}-${i}`} to="/servicos-consulares" data-testid={i < services.length ? `service-card-${svc.id}` : undefined}
            className="group bg-white border border-gray-100 rounded-xl p-5 hover:border-[#CC0000] hover:shadow-lg transition-all text-center flex-shrink-0 w-[160px]">
            <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center text-[#CC0000] mx-auto mb-2 group-hover:bg-[#CC0000] group-hover:text-white transition-colors">
              <FileText size={20} />
            </div>
            <h3 className="font-semibold text-gray-900 text-xs group-hover:text-[#CC0000] transition-colors leading-tight">{svc.name}</h3>
          </Link>
        ))}
      </div>
    </div>
  );
}

// Hero Carousel - Removed buttons
function HeroCarousel({ t }) {
  const [slide, setSlide] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setSlide(s => (s + 1) % HERO_SLIDES.length), 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section className="relative h-[550px] md:h-[600px] overflow-hidden" data-testid="hero-carousel">
      {HERO_SLIDES.map((s, i) => (
        <div key={i} className={`absolute inset-0 transition-opacity duration-1000 ${i === slide ? 'opacity-100' : 'opacity-0'}`}>
          <img src={s.bg} alt={s.tag} className="w-full h-full object-cover" style={{ objectPosition: s.pos }} />
          <div className="hero-gradient absolute inset-0" />
        </div>
      ))}
      <div className="relative z-10 flex flex-col items-center justify-center h-full text-center text-white px-4">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 text-xs font-medium mb-4 fade-in-up">
          <Shield size={12} />
          <span>{HERO_SLIDES[slide].tag}</span>
        </div>
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-4 fade-in-up max-w-4xl">
          {t('home.hero_title')}
        </h1>
        <p className="text-base md:text-lg text-white/85 max-w-2xl fade-in-up">
          {t('home.hero_subtitle')}
        </p>
        <div className="flex gap-2 mt-8">
          {HERO_SLIDES.map((_, i) => (
            <button key={i} onClick={() => setSlide(i)}
              className={`h-1.5 rounded-full transition-all ${i === slide ? 'w-8 bg-white' : 'w-3 bg-white/40'}`} />
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const { t } = useLanguage();

  return (
    <div>
      <HeroCarousel t={t} />

      {/* Quick Services Slideshow - Simplified */}
      <section className="py-12 bg-white" data-testid="quick-services-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900">{t('home.services_title')}</h2>
            <div className="w-16 h-1 bg-[#CC0000] mx-auto mt-3" />
          </div>
          <ServicesCarousel />
          <div className="text-center mt-6">
            <Link to="/servicos-consulares" className="inline-flex items-center gap-2 text-[#CC0000] font-medium hover:underline">
              {t('home.view_all_services')} <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Latest News - Featured External News */}
      <section className="py-12 bg-[#F8F9FA]" data-testid="latest-news-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900">{t('home.news_title')}</h2>
              <div className="w-16 h-1 bg-[#CC0000] mt-3" />
            </div>
            <Link to="/noticias" className="flex items-center gap-1 text-sm text-[#CC0000] font-medium hover:underline">
              {t('common.view_all')} <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {FEATURED_NEWS.map(news => (
              <a 
                key={news.id} 
                href={news.sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="group block"
                data-testid={`featured-news-${news.id}`}
              >
                <article className="bg-white border border-gray-100 rounded-lg overflow-hidden card-hover h-full flex flex-col">
                  <div className="relative h-48 overflow-hidden bg-gray-100">
                    <img 
                      src={news.image} 
                      alt={news.title} 
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" 
                    />
                    <span className="absolute top-2 left-2 px-2 py-1 bg-[#CC0000] text-white text-xs font-medium rounded flex items-center gap-1">
                      <ExternalLink size={10} /> {news.source}
                    </span>
                  </div>
                  <div className="p-4 flex flex-col flex-1">
                    <h3 className="font-semibold text-gray-900 text-sm leading-snug mb-2 group-hover:text-[#CC0000] transition-colors line-clamp-2">
                      {news.title}
                    </h3>
                    <div className="mt-auto flex items-center justify-between text-xs text-gray-400">
                      <span>{news.date}</span>
                      <span className="text-[#CC0000] font-medium flex items-center gap-1">
                        {t('home.read_article')} <ExternalLink size={10} />
                      </span>
                    </div>
                  </div>
                </article>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Actions */}
      <section className="py-12 bg-[#0A0A0A]" data-testid="quick-actions-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link to="/agendamento" className="flex items-center gap-4 p-6 bg-white/5 rounded-xl border border-white/10 hover:bg-white/10 transition-all group">
              <div className="w-12 h-12 bg-[#CC0000] rounded-lg flex items-center justify-center">
                <Calendar size={24} className="text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-white group-hover:text-[#FFD700] transition-colors">{t('home.schedule_appointment')}</h3>
                <p className="text-sm text-gray-400">{t('home.schedule_desc')}</p>
              </div>
            </Link>
            <Link to="/cartao-consular" className="flex items-center gap-4 p-6 bg-white/5 rounded-xl border border-white/10 hover:bg-white/10 transition-all group">
              <div className="w-12 h-12 bg-[#CC0000] rounded-lg flex items-center justify-center">
                <FileText size={24} className="text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-white group-hover:text-[#FFD700] transition-colors">{t('home.consular_card')}</h3>
                <p className="text-sm text-gray-400">{t('home.consular_card_desc')}</p>
              </div>
            </Link>
            <Link to="/servicos-consulares" className="flex items-center gap-4 p-6 bg-white/5 rounded-xl border border-white/10 hover:bg-white/10 transition-all group">
              <div className="w-12 h-12 bg-[#CC0000] rounded-lg flex items-center justify-center">
                <FileText size={24} className="text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-white group-hover:text-[#FFD700] transition-colors">{t('home.consular_services')}</h3>
                <p className="text-sm text-gray-400">{t('home.consular_services_desc')}</p>
              </div>
            </Link>
            <Link to="/salvo-conduto" className="flex items-center gap-4 p-6 bg-white/5 rounded-xl border border-white/10 hover:bg-white/10 transition-all group">
              <div className="w-12 h-12 bg-[#CC0000] rounded-lg flex items-center justify-center">
                <Shield size={24} className="text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-white group-hover:text-[#FFD700] transition-colors">Salvo-Conduto</h3>
                <p className="text-sm text-gray-400">Documento de viagem de emergência</p>
              </div>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
